// TOUR-4b — global intent match + access gates (no current-bucket filter).
// `bucketRelevance` on artifacts is metadata for catalog/docs only until
// a bucket gate lands; Ask Firefly may start manager-bucket tours from sales
// modes when intent + role + mode access pass (see tour-kb-author tests).

import type { AuthContextValue } from "@/contexts/AuthContext";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import {
  KNOWN_CAPABILITIES,
  NO_CAPABILITIES,
  type Capability,
  type HasCapabilityFn,
} from "@/lib/capabilities/capabilities";
import { resolveCapabilities } from "@/lib/capabilities/capabilityResolution";
import { SALES_REP_ROLES } from "@/lib/roles/turboSalesRoles";
import { bestIntentMatchScore } from "./matchAskQuery";
import type { FireflyTourArtifact, FireflyTourResolution } from "./tourTypes";
import { ASK_MIN_MATCH_SCORE } from "./resolveFireflyAsk";

export type CanAccessPrimaryModeFn = (
  mode: PrimaryMode,
  auth: AuthContextValue,
) => boolean;

export type ResolveFireflyTourInput = {
  queryText?: string;
  tourId?: string;
  artifacts: readonly FireflyTourArtifact[];
  auth: AuthContextValue;
  currentMode: PrimaryMode;
  canAccessPrimaryMode: CanAccessPrimaryModeFn;
  /** When omitted, derives role defaults only (no localStorage overrides). */
  hasCapability?: HasCapabilityFn;
};

function isKnownCapability(id: string): id is Capability {
  return (KNOWN_CAPABILITIES as readonly string[]).includes(id);
}

function effectiveHasCapability(input: ResolveFireflyTourInput): HasCapabilityFn {
  if (input.hasCapability) return input.hasCapability;
  if (input.auth.status !== "ready") return NO_CAPABILITIES;
  return resolveCapabilities({}, (...roles) => input.auth.hasRole(...roles));
}

function passesRequiredCapabilities(
  artifact: FireflyTourArtifact,
  auth: AuthContextValue,
  hasCapability: HasCapabilityFn,
): boolean {
  const required = artifact.requiredCapabilities;
  if (!required?.length) return true;
  return required.every((cap) => {
    if (cap === "hasSalesFloor") return auth.hasSalesFloor;
    if (isKnownCapability(cap)) return hasCapability(cap);
    return false;
  });
}

function passesRoleGate(artifact: FireflyTourArtifact, auth: AuthContextValue): boolean {
  if (!artifact.requiredRoles?.length) return true;
  const roles = artifact.requiredRoles;
  if (roles.includes("sales") && auth.hasRole(...SALES_REP_ROLES)) return true;
  const nonSales = roles.filter((r) => r !== "sales");
  if (nonSales.length > 0 && auth.hasRole(...nonSales)) return true;
  return false;
}

function denyNoAccess(targetMode?: PrimaryMode): FireflyTourResolution {
  const message = targetMode
    ? `You don't have access to ${targetMode} in this workspace. Ask your admin if you need that surface.`
    : "You don't have access to that tour from this seat.";
  return { kind: "denied", reason: "no_access", targetMode, message };
}

function canRunTourSteps(
  artifact: FireflyTourArtifact,
  canAccessPrimaryMode: CanAccessPrimaryModeFn,
  auth: AuthContextValue,
): { ok: true; targetMode: PrimaryMode } | { ok: false; targetMode?: PrimaryMode } {
  const modes = new Set(artifact.steps.map((s) => s.target.mode));
  for (const mode of modes) {
    if (!canAccessPrimaryMode(mode, auth)) {
      return { ok: false, targetMode: mode };
    }
  }
  const firstMode = artifact.steps[0]?.target.mode;
  if (!firstMode) return { ok: false };
  return { ok: true, targetMode: firstMode };
}

function matchArtifact(input: ResolveFireflyTourInput): FireflyTourArtifact | null {
  if (input.tourId) {
    const tourId = input.tourId.trim();
    return input.artifacts.find((a) => a.id === tourId) ?? null;
  }
  const query = input.queryText?.trim();
  if (!query) return null;

  let best: { artifact: FireflyTourArtifact; score: number } | null = null;
  for (const artifact of input.artifacts) {
    const score = bestIntentMatchScore(query, artifact.intents);
    if (score < ASK_MIN_MATCH_SCORE) continue;
    if (!best || score > best.score || (score === best.score && artifact.id < best.artifact.id)) {
      best = { artifact, score };
    }
  }
  return best?.artifact ?? null;
}

export function resolveFireflyTour(input: ResolveFireflyTourInput): FireflyTourResolution {
  if (input.auth.status !== "ready") {
    return { kind: "error", message: "Auth not ready" };
  }

  const artifact = matchArtifact(input);
  if (!artifact) {
    return { kind: "denied", reason: "no_match" };
  }

  if (!passesRoleGate(artifact, input.auth)) {
    return denyNoAccess(artifact.steps[0]?.target.mode);
  }

  const hasCapability = effectiveHasCapability(input);
  if (!passesRequiredCapabilities(artifact, input.auth, hasCapability)) {
    return denyNoAccess(artifact.steps[0]?.target.mode);
  }

  const access = canRunTourSteps(artifact, input.canAccessPrimaryMode, input.auth);
  if (!access.ok) {
    return denyNoAccess(access.targetMode ?? artifact.steps[0]?.target.mode);
  }

  return { kind: "start", artifact, targetMode: access.targetMode };
}

export function fireflyTourRequestOutcome(
  resolution: FireflyTourResolution,
): "started" | "denied_permission" | "not_found" | "error" {
  if (resolution.kind === "start") return "started";
  if (resolution.kind === "error") return "error";
  if (resolution.kind === "denied" && resolution.reason === "no_match") return "not_found";
  return "denied_permission";
}
